import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ChevronDown, ChevronRight, Car, Home, Palmtree, Shirt, Sofa, Baby, Smartphone, Gamepad2, Wrench, HardHat, Palette, MoreHorizontal, LayoutGrid } from 'lucide-react';
import { Input } from '../ui/input';
import { CATEGORIES, getLocalizedName } from '../../data/categories';
import { useLanguage } from '../../context/LanguageContext';
import { cn } from '../../lib/utils';

export const CATEGORY_ICONS = {
  vehicules: Car, immobilier: Home, vacances: Palmtree, mode: Shirt, 'maison-jardin': Sofa, famille: Baby,
  electronique: Smartphone, loisirs: Gamepad2, services: Wrench, 'materiel-pro': HardHat, art: Palette, autres: MoreHorizontal,
};

export const CATEGORY_TINTS = {
  vehicules: 'bg-red-50 text-red-600', immobilier: 'bg-emerald-50 text-emerald-600', vacances: 'bg-sky-50 text-sky-600',
  mode: 'bg-pink-50 text-pink-600', 'maison-jardin': 'bg-amber-50 text-amber-600', famille: 'bg-violet-50 text-violet-600',
  electronique: 'bg-blue-50 text-blue-600', loisirs: 'bg-orange-50 text-orange-600', services: 'bg-teal-50 text-teal-600',
  'materiel-pro': 'bg-yellow-50 text-yellow-700', art: 'bg-fuchsia-50 text-fuchsia-600', autres: 'bg-gray-100 text-gray-600',
};

export const subcategoryPath = (catId, subId, transaction) =>
  `/category/${catId}?subcategory=${subId}${transaction ? `&transaction=${transaction}` : ''}`;

export const CategoryIcon = ({ id, className = 'h-5 w-5' }) => {
  const Icon = CATEGORY_ICONS[id] || LayoutGrid;
  return <Icon className={className} />;
};

const SubcategoryRow = ({ cat, sub, language, onNavigate }) => (
  <li>
    {sub.transactionTypes ? (
      <div className="flex items-center justify-between py-2 pl-12 pr-3">
        <Link to={subcategoryPath(cat.id, sub.id)} onClick={onNavigate} className="text-sm text-gray-700 hover:text-emerald-700" data-testid={`browser-sub-${sub.id}`}>
          {getLocalizedName(sub, language)}
        </Link>
        <div className="flex gap-1">
          {sub.transactionTypes.map((tt) => (
            <Link key={tt.id} to={subcategoryPath(cat.id, sub.id, tt.id)} onClick={onNavigate}
                  className="text-[11px] px-2 py-0.5 rounded-full border border-gray-200 text-gray-600 hover:border-emerald-500 hover:text-emerald-700"
                  data-testid={`browser-sub-${sub.id}-${tt.id}`}>
              {getLocalizedName(tt, language)}
            </Link>
          ))}
        </div>
      </div>
    ) : (
      <Link to={subcategoryPath(cat.id, sub.id)} onClick={onNavigate}
            className="flex items-center justify-between py-2 pl-12 pr-3 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
            data-testid={`browser-sub-${sub.id}`}>
        {getLocalizedName(sub, language)}
        <ChevronRight className="h-3.5 w-3.5 text-gray-300" />
      </Link>
    )}
  </li>
);

export const CategoryBrowser = ({ counts = {}, onNavigate, showSearch = true, className }) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState('');

  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    onNavigate?.();
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  return (
    <div className={cn('flex flex-col', className)} data-testid="category-browser">
      {showSearch && (
        <form onSubmit={submit} className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('header.searchPlaceholder')} className="pl-9 h-11 rounded-xl bg-gray-50" data-testid="browser-search-input" />
        </form>
      )}
      <p className="px-1 text-[11px] uppercase tracking-wide font-semibold text-gray-400 mb-1">{t('nav.allCategories')}</p>
      <ul className="divide-y divide-gray-100">
        {CATEGORIES.map((cat) => {
          const expanded = open === cat.id;
          const count = counts[cat.id];
          return (
            <li key={cat.id}>
              <div className="flex items-center">
                <button type="button" onClick={() => setOpen(expanded ? null : cat.id)}
                        className="flex-1 flex items-center gap-3 py-2.5 px-1 text-left" aria-expanded={expanded} data-testid={`browser-cat-${cat.id}`}>
                  <span className={cn('h-9 w-9 rounded-xl flex items-center justify-center shrink-0', CATEGORY_TINTS[cat.id])}><CategoryIcon id={cat.id} /></span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-gray-900">{getLocalizedName(cat, language)}</span>
                    {typeof count === 'number' && <span className="block text-[11px] text-gray-400">{count.toLocaleString()} {t(count === 1 ? 'browser.ad' : 'browser.ads')}</span>}
                  </span>
                  <ChevronDown className={cn('h-4 w-4 text-gray-400 transition-transform', expanded && 'rotate-180')} />
                </button>
              </div>
              {expanded && (
                <ul className="pb-2 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`browser-subs-${cat.id}`}>
                  <li>
                    <Link to={`/category/${cat.id}`} onClick={onNavigate} className="flex items-center gap-1 py-2 pl-12 pr-3 text-sm font-medium text-emerald-700 hover:bg-emerald-50" data-testid={`browser-all-${cat.id}`}>
                      {t('browser.viewAll', { name: getLocalizedName(cat, language) })} <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                  </li>
                  {cat.subcategories.map((sub) => <SubcategoryRow key={sub.id} cat={cat} sub={sub} language={language} onNavigate={onNavigate} />)}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default CategoryBrowser;
